import React, { useState, useEffect } from "react";
import {
	Box,
	Typography,
	Button,
	TextField,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Checkbox,
	FormControlLabel,
	Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

function AbaProcessos({ processos, setProcessos, onAddProcessoClick }) {
	const [openDialog, setOpenDialog] = useState(false);
	const [processoAtual, setProcessoAtual] = useState(null);
	const [nomeProcesso, setNomeProcesso] = useState("");
	const [etapas, setEtapas] = useState([]);

	// Expor handleOpenDialog para o componente pai
	useEffect(() => {
		if (onAddProcessoClick) {
			onAddProcessoClick(() => () => handleOpenDialog());
		}
	}, [onAddProcessoClick]);

	const handleOpenDialog = (processo = null) => {
		if (processo) {
			setProcessoAtual(processo);
			setNomeProcesso(processo.nome);
			setEtapas(processo.etapas || []);
		} else {
			setProcessoAtual(null);
			setNomeProcesso("");
			setEtapas([]);
		}
		setOpenDialog(true);
	};

	const handleCloseDialog = () => {
		setOpenDialog(false);
		setProcessoAtual(null);
		setNomeProcesso("");
		setEtapas([]);
	};

	const handleAddEtapa = () => {
		const novaEtapa = {
			id: Date.now(),
			nome: "",
			posicao: etapas.length + 1,
			solicitaProduto: false,
		};
		setEtapas([...etapas, novaEtapa]);
	};

	const handleRemoveEtapa = (id) => {
		const novasEtapas = etapas
			.filter((etapa) => etapa.id !== id)
			.map((etapa, index) => ({
				...etapa,
				posicao: index + 1,
			}));
		setEtapas(novasEtapas);
	};

	const handleEtapaChange = (id, field, value) => {
		setEtapas(
			etapas.map((etapa) =>
				etapa.id === id ? { ...etapa, [field]: value } : etapa
			)
		);
	};

	const handleMoveEtapa = (index, direction) => {
		const newIndex = direction === "up" ? index - 1 : index + 1;
		if (newIndex < 0 || newIndex >= etapas.length) return;

		const novasEtapas = [...etapas];
		[novasEtapas[index], novasEtapas[newIndex]] = [
			novasEtapas[newIndex],
			novasEtapas[index],
		];

		// Atualizar posições
		const etapasOrdenadas = novasEtapas.map((etapa, idx) => ({
			...etapa,
			posicao: idx + 1,
		}));

		setEtapas(etapasOrdenadas);
	};

	const handleSaveProcesso = () => {
		if (!nomeProcesso.trim()) {
			alert("Digite o nome do processo");
			return;
		}

		if (etapas.length === 0) {
			alert("Adicione pelo menos uma etapa ao processo");
			return;
		}

		// Verificar se todas as etapas têm nome
		const etapasSemNome = etapas.filter((etapa) => !etapa.nome.trim());
		if (etapasSemNome.length > 0) {
			alert("Todas as etapas devem ter um nome");
			return;
		}

		const novoProcesso = {
			id: processoAtual?.id || Date.now(),
			nome: nomeProcesso,
			etapas: etapas.map((etapa) => ({
				...etapa,
				solicitaProduto: Boolean(etapa.solicitaProduto),
			})),
		};

		if (processoAtual) {
			// Editar processo existente
			setProcessos(
				processos.map((p) => (p.id === processoAtual.id ? novoProcesso : p))
			);
		} else {
			// Adicionar novo processo
			setProcessos([...processos, novoProcesso]);
		}

		handleCloseDialog();
	};

	const handleDeleteProcesso = (id) => {
		if (window.confirm("Deseja realmente excluir este processo?")) {
			setProcessos(processos.filter((p) => p.id !== id));
		}
	};

	return (
		<Box>
			<Box sx={{ mb: 3 }}>
				<Typography
					variant="subtitle2"
					sx={{
						color: "#666",
						fontWeight: 600,
						fontSize: "13px",
						textTransform: "uppercase",
						letterSpacing: "0.5px",
					}}
				>
					Processos Cadastrados
				</Typography>
				{processos.length > 0 && (
					<Typography
						variant="caption"
						sx={{
							color: "#999",
							fontSize: "13px",
							mt: 0.5,
							display: "block",
						}}
					>
						{processos.length} {processos.length === 1 ? "processo cadastrado" : "processos cadastrados"}
					</Typography>
				)}
			</Box>

			{processos.length === 0 ? (
				<Box
					sx={{
						textAlign: "center",
						py: 6,
						px: 3,
						border: "2px dashed #e0e0e0",
						borderRadius: "8px",
						bgcolor: "#fafafa",
					}}
				>
					<Box
						sx={{
							width: 56,
							height: 56,
							borderRadius: "50%",
							bgcolor: "#E7F6F8",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							margin: "0 auto",
							mb: 2,
						}}
					>
						<AddIcon sx={{ color: "#1a9e8f", fontSize: 28 }} />
					</Box>
					<Typography
						variant="body2"
						sx={{
							fontSize: "15px",
							color: "#666",
							fontWeight: 500,
							mb: 0.5,
						}}
					>
						Nenhum processo cadastrado
					</Typography>
					<Typography
						variant="caption"
						sx={{
							fontSize: "13px",
							color: "#999",
						}}
					>
						Clique em "Adicionar processo" para criar seu primeiro processo
					</Typography>
				</Box>
			) : (
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
					{processos.map((processo) => (
						<Box
							key={processo.id}
							sx={{
								border: "1px solid #e0e0e0",
								borderRadius: "8px",
								bgcolor: "white",
								p: 2.5,
								display: "flex",
								alignItems: "center",
								gap: 3,
								transition: "all 0.2s ease",
								"&:hover": {
									boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
									borderColor: "#1a9e8f",
									transform: "translateY(-1px)",
								},
							}}
						>
							{/* Ícone/Badge do Processo */}
							<Box
								sx={{
									width: 56,
									height: 56,
									borderRadius: "8px",
									bgcolor: "linear-gradient(135deg, #E7F6F8 0%, #C5EDF5 100%)",
									background: "linear-gradient(135deg, #E7F6F8 0%, #C5EDF5 100%)",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									flexShrink: 0,
									border: "2px solid #D1F0F6",
								}}
							>
								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										alignItems: "center",
										gap: 0.25,
									}}
								>
									<Typography
										sx={{
											color: "#1a9e8f",
											fontWeight: 800,
											fontSize: "20px",
											lineHeight: 1,
										}}
									>
										{processo.etapas.length}
									</Typography>
									<Typography
										sx={{
											color: "#1a9e8f",
											fontWeight: 600,
											fontSize: "9px",
											textTransform: "uppercase",
											letterSpacing: "0.3px",
										}}
									>
										{processo.etapas.length === 1 ? "etapa" : "etapas"}
									</Typography>
								</Box>
							</Box>

							{/* Informações do Processo */}
							<Box sx={{ flex: 1, minWidth: 0 }}>
								<Typography
									variant="subtitle1"
									sx={{
										fontWeight: 600,
										color: "#333",
										fontSize: "16px",
										mb: 0.75,
										lineHeight: 1.3,
									}}
								>
									{processo.nome}
								</Typography>
								<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
										<Box
											sx={{
												width: 6,
												height: 6,
												borderRadius: "50%",
												bgcolor: "#1a9e8f",
											}}
										/>
										<Typography
											variant="caption"
											sx={{
												color: "#666",
												fontSize: "13px",
											}}
										>
											Processo ativo
										</Typography>
									</Box>
									<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
										<Typography
											variant="caption"
											sx={{
												color: "#999",
												fontSize: "13px",
											}}
										>
											{processo.etapas
												.sort((a, b) => a.posicao - b.posicao)
												.map((etapa) =>
													etapa.solicitaProduto ? `${etapa.nome} (produto)` : etapa.nome
												)
												.join(" → ")}
										</Typography>
									</Box>
								</Box>
							</Box>

							{/* Ações */}
							<Box
								sx={{
									display: "flex",
									gap: 1,
									flexShrink: 0,
								}}
							>
								<IconButton
									onClick={() => handleOpenDialog(processo)}
									sx={{
										width: 36,
										height: 36,
										bgcolor: "#F0F9FA",
										color: "#1a9e8f",
										"&:hover": {
											bgcolor: "#E7F6F8",
											transform: "scale(1.05)",
										},
										transition: "all 0.2s ease",
									}}
								>
									<EditIcon fontSize="small" />
								</IconButton>
								<IconButton
									onClick={() => handleDeleteProcesso(processo.id)}
									sx={{
										width: 36,
										height: 36,
										bgcolor: "#FEF2F2",
										color: "#ff4d4d",
										"&:hover": {
											bgcolor: "#FEE2E2",
											transform: "scale(1.05)",
										},
										transition: "all 0.2s ease",
									}}
								>
									<DeleteIcon fontSize="small" />
								</IconButton>
							</Box>
						</Box>
					))}
				</Box>
			)}

			{/* Dialog para adicionar/editar processo */}
			<Dialog
				open={openDialog}
				onClose={handleCloseDialog}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle sx={{ bgcolor: "#1a9e8f", color: "white", fontSize: "18px", fontWeight: 600, py: 2.5 }}>
					{processoAtual ? "Editar Processo" : "Adicionar Processo"}
				</DialogTitle>
				<DialogContent sx={{ bgcolor: "#fafafa" }}>
					<Box sx={{ py: 3 }}>
						{/* Campo Nome do Processo */}
						<Box
							sx={{
								bgcolor: "white",
								borderRadius: "8px",
								p: 3,
								mb: 3,
								boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
							}}
						>
							<Typography
								variant="subtitle2"
								sx={{
									fontWeight: 600,
									fontSize: "13px",
									color: "#666",
									mb: 2,
									textTransform: "uppercase",
									letterSpacing: "0.5px",
								}}
							>
								Informações Básicas
							</Typography>
							<TextField
								fullWidth
								label="Nome do Processo"
								value={nomeProcesso}
								onChange={(e) => setNomeProcesso(e.target.value)}
								required
								placeholder="Ex: Processo de distribuição de defensivos"
								InputLabelProps={{ style: { fontSize: "15px" } }}
								inputProps={{ style: { fontSize: "15px" } }}
								sx={{
									"& .MuiOutlinedInput-root": {
										bgcolor: "white",
									},
								}}
							/>
						</Box>

						{/* Seção de Etapas */}
						<Box
							sx={{
								bgcolor: "white",
								borderRadius: "8px",
								p: 3,
								boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
							}}
						>
							<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									mb: 3,
								}}
							>
								<Box>
									<Typography
										variant="subtitle2"
										sx={{
											fontWeight: 600,
											fontSize: "13px",
											color: "#666",
											textTransform: "uppercase",
											letterSpacing: "0.5px",
										}}
									>
										Etapas do Processo
									</Typography>
									<Typography
										variant="caption"
										sx={{
											color: "#999",
											fontSize: "13px",
											mt: 0.5,
											display: "block",
										}}
									>
										{etapas.length > 0
											? `${etapas.length} ${etapas.length === 1 ? "etapa adicionada" : "etapas adicionadas"}`
											: "Nenhuma etapa adicionada"}
									</Typography>
								</Box>
								<Button
									variant="contained"
									startIcon={<AddIcon />}
									onClick={handleAddEtapa}
									sx={{
										textTransform: "none",
										bgcolor: "#1a9e8f",
										color: "white",
										fontSize: "14px",
										px: 2.5,
										py: 1,
										boxShadow: "none",
										"&:hover": {
											bgcolor: "#138a7d",
											boxShadow: "0 2px 4px rgba(23, 162, 184, 0.3)",
										},
									}}
								>
									Nova Etapa
								</Button>
							</Box>

							{etapas.length === 0 ? (
								<Box
									sx={{
										textAlign: "center",
										py: 5,
										px: 3,
										border: "2px dashed #e0e0e0",
										borderRadius: "8px",
										bgcolor: "#f9f9f9",
									}}
								>
									<Box
										sx={{
											width: 48,
											height: 48,
											borderRadius: "50%",
											bgcolor: "#E7F6F8",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											margin: "0 auto",
											mb: 2,
										}}
									>
										<AddIcon sx={{ color: "#1a9e8f", fontSize: 24 }} />
									</Box>
									<Typography
										variant="body2"
										sx={{
											fontSize: "14px",
											color: "#666",
											fontWeight: 500,
											mb: 0.5,
										}}
									>
										Nenhuma etapa adicionada
									</Typography>
									<Typography
										variant="caption"
										sx={{
											fontSize: "13px",
											color: "#999",
										}}
									>
										Clique em "Nova Etapa" para começar
									</Typography>
								</Box>
							) : (
								<Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
									{etapas
										.sort((a, b) => a.posicao - b.posicao)
										.map((etapa, index) => (
											<Box
												key={etapa.id}
												sx={{
													border: "1px solid #e8e8e8",
													borderRadius: "8px",
													bgcolor: "#fafafa",
													overflow: "hidden",
													transition: "all 0.2s ease",
													"&:hover": {
														boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
														borderColor: "#1a9e8f",
													},
												}}
											>
												<Box
													sx={{
														display: "flex",
														alignItems: "center",
														gap: 2.5,
														p: 2.5,
													}}
												>
													{/* Número da Etapa */}
													<Box
														sx={{
															width: 40,
															height: 40,
															borderRadius: "8px",
															bgcolor: "#E7F6F8",
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															flexShrink: 0,
														}}
													>
														<Typography
															variant="subtitle1"
															sx={{
																color: "#1a9e8f",
																fontWeight: 700,
																fontSize: "16px",
															}}
														>
															{etapa.posicao}
														</Typography>
													</Box>

													{/* Campo de Input */}
													<Box sx={{ flex: 1 }}>
														<TextField
															fullWidth
															label="Nome da Etapa"
															value={etapa.nome}
															onChange={(e) =>
																handleEtapaChange(
																	etapa.id,
																	"nome",
																	e.target.value
																)
															}
															placeholder="Ex: Recebimento do pedido"
															InputLabelProps={{ style: { fontSize: "14px" } }}
															inputProps={{ style: { fontSize: "15px" } }}
															required
															sx={{
																"& .MuiOutlinedInput-root": {
																	bgcolor: "white",
																},
															}}
														/>
													</Box>

													{/* Etapas marcadas pedem, na ordem de produção, um produto
														vindo de uma OP anterior. */}
													<Tooltip title="Na ordem de produção esta etapa vai pedir um produto de uma OP anterior e a quantidade utilizada">
														<FormControlLabel
															sx={{ flexShrink: 0, mr: 0 }}
															control={
																<Checkbox
																	checked={Boolean(etapa.solicitaProduto)}
																	onChange={(e) =>
																		handleEtapaChange(
																			etapa.id,
																			"solicitaProduto",
																			e.target.checked
																		)
																	}
																	sx={{
																		color: "#1a9e8f",
																		"&.Mui-checked": { color: "#1a9e8f" },
																	}}
																/>
															}
															label={
																<Typography sx={{ fontSize: "14px", color: "#666" }}>
																	Solicita produto
																</Typography>
															}
														/>
													</Tooltip>

													{/* Botões de Ação */}
													<Box
														sx={{
															display: "flex",
															gap: 0.5,
															flexShrink: 0,
														}}
													>
														<IconButton
															size="small"
															onClick={() => handleMoveEtapa(index, "up")}
															disabled={index === 0}
															sx={{
																color: "#1a9e8f",
																"&:disabled": {
																	color: "#ccc",
																},
																"&:hover": {
																	bgcolor: "rgba(23, 162, 184, 0.08)",
																},
															}}
														>
															<ArrowUpwardIcon fontSize="small" />
														</IconButton>
														<IconButton
															size="small"
															onClick={() => handleMoveEtapa(index, "down")}
															disabled={index === etapas.length - 1}
															sx={{
																color: "#1a9e8f",
																"&:disabled": {
																	color: "#ccc",
																},
																"&:hover": {
																	bgcolor: "rgba(23, 162, 184, 0.08)",
																},
															}}
														>
															<ArrowDownwardIcon fontSize="small" />
														</IconButton>
														<IconButton
															onClick={() => handleRemoveEtapa(etapa.id)}
															size="small"
															sx={{
																color: "#ff4d4d",
																"&:hover": {
																	bgcolor: "rgba(220, 53, 69, 0.08)",
																},
															}}
														>
															<DeleteIcon fontSize="small" />
														</IconButton>
													</Box>
												</Box>
											</Box>
										))}
								</Box>
							)}
						</Box>
					</Box>
				</DialogContent>
				<DialogActions sx={{ p: 2, gap: 1 }}>
					<Button
						onClick={handleCloseDialog}
						sx={{
							textTransform: "none",
							color: "#666",
							fontSize: "15px",
							"&:hover": {
								bgcolor: "rgba(0, 0, 0, 0.04)",
							},
						}}
					>
						Cancelar
					</Button>
					<Button
						onClick={handleSaveProcesso}
						variant="contained"
						sx={{
							bgcolor: "#1a9e8f",
							"&:hover": {
								bgcolor: "#138a7d",
							},
							textTransform: "none",
							fontSize: "15px",
							px: 3,
						}}
					>
						Salvar
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}

export default AbaProcessos;
